import { Button, Dialog, Input, Select } from '@artosera/ui';
import {
  RUBRIQUES_CV,
  type SaisieCv,
  type EntreeCvRendue,
  type RubriqueCv,
} from '@artosera/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCvArtiste, useModifierCv } from '../../api/cvArtiste.js';
import { estApiError } from '../../api/errors.js';
import { Field } from '../Field.js';
import styles from './DossierArtiste.module.css';

export function CvArtisteTab({
  galerieSlug,
  artisteId,
  peutEcrire,
}: {
  galerieSlug: string;
  artisteId: string;
  peutEcrire: boolean;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const cv = useCvArtiste(galerieSlug, artisteId);
  const modifier = useModifierCv(galerieSlug, artisteId);
  const [edition, setEdition] = useState<EntreeCvRendue | 'nouvelle' | null>(null);
  const [confirmation, setConfirmation] = useState(false);
  const [actualise, setActualise] = useState(false);
  const tr = (cle: string) => t(`regie:cvArtiste.${cle}`);
  return (
    <section className={styles.section}>
      <h2>{tr('titre')}</h2>
      {cv.isLoading && <p>{t('commun:chargement')}</p>}
      {cv.isError && (
        <p role="alert">{estApiError(cv.error) ? cv.error.message : t('commun:erreurReseau')}</p>
      )}
      {cv.data?.rubriques.length === 0 && <p>{tr('vide')}</p>}
      {cv.data?.rubriques.map((groupe) => (
        <div key={groupe.rubrique}>
          <h3>{t(`commun:cvArtiste.rubriques.${groupe.rubrique}`)}</h3>
          <ul className={styles.liste}>
            {groupe.entrees.map((entree) => (
              <li key={`${entree.origine}-${entree.id}`} className={styles.entree}>
                <div>
                  <strong>{entree.annee}</strong> —{' '}
                  {i18n.language === 'en'
                    ? entree.intitule.en || entree.intitule.fr
                    : entree.intitule.fr}
                  <p>{[entree.lieu, entree.ville, entree.pays].filter(Boolean).join(' · ')}</p>
                  {entree.origine === 'exposition' && (
                    <small>
                      {t('regie:cvArtiste.depuisExposition', { titre: entree.intitule.fr })}
                    </small>
                  )}
                </div>
                {peutEcrire && (
                  <div className={styles.actions}>
                    {entree.origine === 'exposition' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={modifier.isPending}
                        onClick={() =>
                          modifier.mutate({ type: 'masquer', expositionId: entree.id })
                        }
                      >
                        {tr('masquer')}
                      </Button>
                    ) : (
                      <>
                        <Button variant="secondary" size="sm" onClick={() => setEdition(entree)}>
                          {t('commun:actions.modifier')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={modifier.isPending}
                          onClick={() => modifier.mutate({ type: 'supprimer', id: entree.id })}
                        >
                          {t('commun:actions.supprimer')}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {peutEcrire && (
        <div className={styles.actions}>
          <Button onClick={() => setEdition('nouvelle')}>{tr('ajouter')}</Button>
          <Button
            variant="secondary"
            onClick={() => {
              setActualise(false);
              setConfirmation(true);
            }}
          >
            {tr('actualiser')}
          </Button>
        </div>
      )}
      {modifier.isError && (
        <p role="alert">
          {estApiError(modifier.error) ? modifier.error.message : t('commun:erreurReseau')}
        </p>
      )}
      {actualise && <p role="status">{tr('actualise')}</p>}
      {edition !== null && (
        <FormulaireEntreeCv
          key={edition === 'nouvelle' ? 'nouvelle' : edition.id}
          entree={edition === 'nouvelle' ? null : edition}
          attente={modifier.isPending}
          fermer={() => setEdition(null)}
          enregistrer={async (corps) => {
            await modifier.mutateAsync(
              edition === 'nouvelle'
                ? { type: 'ajouter', corps }
                : {
                    type: 'modifier',
                    id: edition.id,
                    corps: { ...corps, pays: corps.pays ?? null },
                  },
            );
            setEdition(null);
          }}
        />
      )}
      <Dialog
        open={confirmation}
        onOpenChange={setConfirmation}
        title={tr('actualiser')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            loading={modifier.isPending}
            onClick={() =>
              modifier.mutate(
                { type: 'texte' },
                {
                  onSuccess: () => {
                    setConfirmation(false);
                    setActualise(true);
                  },
                },
              )
            }
          >
            {t('commun:actions.confirmer')}
          </Button>
        }
      >
        <p>{tr('confirmation')}</p>
        {modifier.isError && (
          <p role="alert">
            {estApiError(modifier.error) ? modifier.error.message : t('commun:erreurReseau')}
          </p>
        )}
      </Dialog>
    </section>
  );
}

function FormulaireEntreeCv({
  entree,
  attente,
  fermer,
  enregistrer,
}: {
  entree: EntreeCvRendue | null;
  attente: boolean;
  fermer: () => void;
  enregistrer: (corps: SaisieCv) => Promise<void>;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const [annee, setAnnee] = useState(String(entree?.annee ?? new Date().getFullYear()));
  const [rubrique, setRubrique] = useState<RubriqueCv>(
    entree?.rubrique ?? 'exposition_personnelle',
  );
  const [fr, setFr] = useState(entree?.intitule.fr ?? '');
  const [en, setEn] = useState(entree?.intitule.en ?? '');
  const [lieu, setLieu] = useState(entree?.lieu ?? '');
  const [ville, setVille] = useState(entree?.ville ?? '');
  const [pays, setPays] = useState(entree?.pays ?? '');
  const [erreur, setErreur] = useState<string | null>(null);
  const tr = (cle: string) => t(`regie:cvArtiste.${cle}`);
  const valide =
    fr.trim() &&
    Number.isInteger(Number(annee)) &&
    Number(annee) >= 1800 &&
    Number(annee) <= new Date().getFullYear() + 2 &&
    (!pays || /^[A-Z]{2}$/.test(pays));
  async function soumettre() {
    setErreur(null);
    try {
      await enregistrer({
        annee: Number(annee),
        rubrique,
        intitule: { fr, en },
        lieu,
        ville,
        ...(pays ? { pays } : {}),
        masquee: false,
      });
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(ouvert) => {
        if (!ouvert) fermer();
      }}
      title={entree ? tr('modifier') : tr('ajouter')}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <Button loading={attente} disabled={!valide} onClick={() => void soumettre()}>
          {t('commun:actions.enregistrer')}
        </Button>
      }
    >
      <div className={styles.formulaire}>
        <Field label={tr('annee')} htmlFor="cv-annee">
          <Input
            id="cv-annee"
            type="number"
            min={1800}
            max={new Date().getFullYear() + 2}
            value={annee}
            onChange={(e) => setAnnee(e.target.value)}
          />
        </Field>
        <Field label={tr('rubrique')} htmlFor="cv-rubrique">
          <Select
            id="cv-rubrique"
            value={rubrique}
            onValueChange={(v) => setRubrique(v as RubriqueCv)}
            options={RUBRIQUES_CV.map((value) => ({
              value,
              label: t(`commun:cvArtiste.rubriques.${value}`),
            }))}
          />
        </Field>
        <Field label={tr('intituleFr')} htmlFor="cv-fr">
          <Input id="cv-fr" value={fr} maxLength={2000} onChange={(e) => setFr(e.target.value)} />
        </Field>
        <Field label={tr('intituleEn')} htmlFor="cv-en">
          <Input id="cv-en" value={en} maxLength={2000} onChange={(e) => setEn(e.target.value)} />
        </Field>
        <Field label={tr('lieu')} htmlFor="cv-lieu">
          <Input
            id="cv-lieu"
            value={lieu}
            maxLength={200}
            onChange={(e) => setLieu(e.target.value)}
          />
        </Field>
        <Field label={tr('ville')} htmlFor="cv-ville">
          <Input
            id="cv-ville"
            value={ville}
            maxLength={200}
            onChange={(e) => setVille(e.target.value)}
          />
        </Field>
        <Field label={tr('pays')} htmlFor="cv-pays">
          <Input
            id="cv-pays"
            value={pays}
            maxLength={2}
            onChange={(e) => setPays(e.target.value.toUpperCase())}
          />
        </Field>
        {erreur && <p role="alert">{erreur}</p>}
      </div>
    </Dialog>
  );
}
